import { useEffect, useMemo, useState } from "react"

import { CheckIcon, PlusIcon, Trash, XIcon } from "lucide-react"
import { Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DefaultPopover, Separator, Input, CommandSearch, DefaultTooltip, CommandItemSearch, CommandGroupSearch } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { useCraftProp } from "../../utils"
import ApplyButtonPopover from "../components/ApplyButtonPopover"

import FilterDropShadow from "./DropShadow"
import Preview from "./Preview"
import FilterProps from "./Props"
import { FiltersProvider, useFilters } from "./context"

export default function FilterWrapper() {
  return <FiltersProvider>
    <FiltersProperty />
  </FiltersProvider>
}

const FiltersProperty = () => {
  const { filters, handleChange, setFilters } = useFilters()
  const { value: filtersCraft, setValue: setFiltersCraft } = useCraftProp('filters')

  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  useEffect(() => {
    setFilters()
  }, [device, selectedUtilities, setFilters])

  useEffect(() => {
    if (filters === undefined) {
      setFilters(filtersCraft || { filter: {} })
    }
  }, [filtersCraft, filters, setFilters])

  const handleDelete = type => {
    setFiltersCraft({ ...filtersCraft, [type]: undefined })
    setFilters({ ...filters || {}, [type]: undefined })
  }

  return <>
    <div className='flex flex-col gap-2 mb-2'>
      {FILTERS.map(({ value, label }) => <>
        {filters?.[value] && <>
          <FilterItem
            type={value}
            label={label}
            onDelete={handleDelete}
          />
        </>}
      </>)}
    </div>
    <DropdownFilters
      filters={filters}
      onAdd={type => handleChange({ type, value: {} })}
    />
  </>
}

const FilterItem = ({ type, label, onDelete }) => <>
  <Poppover label={label} type={type} >
    <div className='flex gap-2 justify-between'>
      <div className='w-full'>
        <InputPreview type={type} />
      </div>
      <Button variant='light' size='sm' onClick={() => onDelete(type)}>
        <Trash size='14' />
      </Button>
    </div>
  </Poppover>
</>

const Poppover = ({ children, label, type }) => {
  const [open, setOpen] = useState(false)
  const [displayProps, setDisplayProps] = useState()
  const [filterType, setFilterType] = useState()

  const { filters, setFilters } = useFilters()
  const { setValue: setFiltersCraft } = useCraftProp('filters')

  const handleClose = () => {
    setFilters({ ...filters, [type]: filterType })
    setDisplayProps(undefined)
    setOpen(false)
  }

  useEffect(() => {
    if (filterType === undefined) {
      setFilterType(filters?.[type])
    }
  }, [filterType, filters, type])

  useEffect(() => {
    if (displayProps === undefined) {
      const itemsKeys = PROPS.map(item => item.value)
      const newDisplayed = itemsKeys.filter(key => filters?.[type][key] !== undefined)
      setDisplayProps(newDisplayed.length ? newDisplayed : ['blur'])
    }
  }, [displayProps, filters, type, open, setFilters])

  const handleAdd = filterName => {
    const isDisplayed = displayProps.includes(filterName)
    return setDisplayProps(isDisplayed ? displayProps.filter(item => item !== filterName) : [...displayProps, filterName])
  }

  const handleApply = () => {
    setFiltersCraft(filters)
    setFilterType(filters?.[type])
  }

  const handleCancel = () => {
    setFilters()
    handleClose()
  }

  const removeProp = prop => {
    setDisplayProps(displayProps.filter(item => item !== prop))
  }

  return <>
    <DefaultPopover
      trigger={children}
      triggerProps={{ asChild: true }}
      contentProps={{ side: 'left', align: 'center', onInteractOutside: handleClose }}
      wrapperProps={{ open: open, onOpenChange: setOpen }}
    >
      <Title
        displayProps={displayProps}
        onAdd={handleAdd}
        onClose={() => handleClose()}
        label={label}
        availableProps={AVAILABLE_PROPS[type]}
      />
      <Separator className='m-0 my-2' />
      <Preview type={type} />
      <Separator className='m-0 my-2' />
      <FilterProps type={type} displayProps={displayProps} removeProp={removeProp} />
      <ApplyButtonPopover
        onApply={handleApply}
        onCancel={handleCancel}
        name='filters'
        value={filters}
      />
    </DefaultPopover>
  </>
}

const Title = ({ displayProps = [], onAdd, onClose, label, availableProps }) => <>
  <div className='flex justify-between items-center'>
    <div className='flex gap-2 items-center '>
      {label}
      <DropdownProps displayProps={displayProps} onAdd={onAdd} availableProps={availableProps} />
    </div>

    <XIcon size='16' className='cursor-pointer' onClick={onClose} />
  </div>
</>

const ButtonAddProps = () => <DefaultTooltip side='right' content='Add transform property' triggerProps={{ asChild: true }}>
  <Button variant='light' size='sm'>
    <PlusIcon size='14' />
  </Button>
</DefaultTooltip>

const DropdownProps = ({ displayProps = [], onAdd, availableProps }) => <>
  <DefaultPopover trigger={<ButtonAddProps />} contentProps={{ side: 'top' }} >
    <CommandSearch placeholder='Type to search a transform...'>
      <CommandGroupSearch heading=''>
        {PROPS.map(({ value, label }) => {
          if (availableProps.includes(value)) {
            return <DropdownItem
              key={value}
              value={value}
              label={label}
              onAdd={onAdd}
              active={displayProps.includes(value)}
            />
          }
        })}
      </CommandGroupSearch>
    </CommandSearch>
  </DefaultPopover>
</>

const DropdownItem = ({ value, onAdd, active, label }) => <>
  <div onClick={() => onAdd(value)} >
    <CommandItemSearch key={value}>
      <div className="flex items-center">
        {label}
        {active && <CheckIcon size='12' className='ml-1' />}
      </div>
    </CommandItemSearch>
  </div>
</>

const InputPreview = ({ type }) => {
  const { value: valueCraft } = useCraftProp('filters')
  const value = useMemo(() => valueCraft?.[type], [valueCraft, type])

  const getStringValue = values => {
    if (typeof values === 'string') {
      return String(values).replace('_', '')
    }

    let { value, color } = values
    value = typeof value === 'string' ? value : [value?.x && value.x.replace('_', ''), value?.y && value.y.replace('_', ''), value?.blur && `blur: ${value.blur.replace('_', '')}`].filter(Boolean).join(', ')
    return [value, color].filter(Boolean).join(' ')
  }

  const inputValue = useMemo(() => {
    const entries = Object.entries(value || {}).filter(([_, value]) => value !== undefined)
    const items = entries.map(([key, value]) => `${key}: ${getStringValue(value)}`)
    const string = [items.length && type, items.length && items].filter(Boolean).join(', ')
    return string
  }, [value, type])

  return <div className='flex items-center gap-1 relative'>
    <Input value={inputValue || ''} readOnly variant='accent' />
  </div>
}

const DropdownFilters = ({ filters, onAdd }) => <>
  <div className='flex justify-end'>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='light' size='sm'> <PlusIcon size='14' /> </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {FILTERS.map(({ value, label }) => <>
          <DropdownMenuItem key={value} onClick={() => onAdd(value)} disabled={filters?.[value]}>
            {label}
          </DropdownMenuItem>
        </>)}
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</>

const FILTERS = [
  { value: 'filter', label: 'Filter' },
  { value: 'backdrop', label: 'Backdrop' },
]

export const PROPS = [
  {
    value: 'blur',
    label: 'Blur',
    defaultValue: '0_px',
    predefined: true,
    predefinedOptions: [
      {
        value: 'xs',
        label: 'Extra small',
      },
      {
        value: 'sm',
        label: 'Small',
      },
      {
        value: 'md',
        label: 'Medium',
      },
      {
        value: 'lg',
        label: 'Large',
      },
      {
        value: 'xl',
        label: 'Extra large',
      },
      {
        value: '2xl',
        label: '2 Extra large',
      },
      {
        value: '3xl',
        label: '3 Extra large',
      },
      {
        value: 'none',
        label: 'None',
      },
    ],
    unitOptions: [
      { label: 'Pixels', value: 'px' },
      { label: 'Rem', value: 'rem' },
      { label: 'Em', value: 'em' },
      { label: 'Viewport Width', value: 'vw' },
      { label: 'Viewport Height', value: 'vh' },
    ],
    slideConfig: {
      min: 0,
      max: 10,
      step: 0.1,
    },
  },
  {
    value: 'brightness',
    label: 'Brightness',
    defaultValue: 100,
    predefined: false,
    unitOptions: [{ label: '%', value: 'unitless' }],
    slideConfig: {
      min: 0,
      max: 200,
      step: 1,
      unit: '%',
    },
  },
  {
    value: 'contrast',
    label: 'Contrast',
    defaultValue: 100,
    predefined: false,
    unitOptions: [{ label: '%', value: 'unitless' }],
    slideConfig: {
      min: 0,
      max: 200,
      step: 1,
      unit: '%',
    },
  },
  {
    value: 'drop-shadow',
    label: 'Drop shadow',
    onlySlide: true,
    customInput: FilterDropShadow,
  },
  {
    value: 'grayscale',
    label: 'Grayscale',
    onlySlide: true,
    defaultValue: 0,
    slideConfig: {
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  },
  {
    value: 'hue-rotate',
    label: 'Hue rotate',
    onlySlide: true,
    defaultValue: 0,
    slideConfig: {
      min: 0,
      max: 360,
      step: 1,
      unit: 'deg',
    },
  },
  {
    value: 'invert',
    label: 'Invert',
    onlySlide: true,
    defaultValue: 0,
    slideConfig: {
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  },
  {
    value: 'saturate',
    label: 'Saturate',
    defaultValue: 100,
    predefined: false,
    unitOptions: [{ label: '%', value: 'unitless' }],
    slideConfig: {
      min: 0,
      max: 200,
      step: 1,
      unit: '%',
    },
  },
  {
    value: 'sepia',
    label: 'Sepia',
    defaultValue: 0,
    predefined: false,
    unitOptions: [{ label: '%', value: 'unitless' }],
    slideConfig: {
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  },
  {
    value: 'opacity',
    label: 'Opacity',
    onlySlide: true,
    defaultValue: 0,
    slideConfig: {
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  },
]

const AVAILABLE_PROPS = {
  filter: ['blur', 'brightness', 'contrast', 'drop-shadow', 'grayscale', 'hue-rotate', 'invert', 'saturate', 'sepia'],
  backdrop: ['blur', 'brightness', 'contrast', 'grayscale', 'hue-rotate', 'invert', 'saturate', 'sepia', 'opacity'],
}
